import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Plus, ShieldCheck, ShieldOff, UserX, UserCheck } from 'lucide-react';
import type { CreateUserRequest, PublicUser } from '@command-room/shared';
import { useUsers, useCreateUser, useUpdateUser, useMe } from '@/api/queries';
import { TableSkeleton } from '@/components/feedback/Skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { relativeTime } from '@/lib/format';

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const createUser = useCreateUser();
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<CreateUserRequest>({
    defaultValues: { role: 'viewer', timezone: 'UTC', locale: 'en' },
  });
  const role = watch('role');

  function onClose(v: boolean) {
    if (!v) reset();
    onOpenChange(v);
  }

  async function onSubmit(data: CreateUserRequest) {
    await createUser.mutateAsync(data);
    onClose(false);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent title="Invite user" description="The user can change their password after first login.">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                {...register('full_name', { required: true })}
                placeholder="Jane Smith"
                className={errors.full_name ? 'border-destructive' : ''}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register('email', { required: true })}
                placeholder="jane@example.com"
                className={errors.email ? 'border-destructive' : ''}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Temporary password</Label>
              <Input
                id="password"
                type="password"
                {...register('password', { required: true, minLength: 12 })}
                placeholder="Min 12 characters"
                className={errors.password ? 'border-destructive' : ''}
              />
              {errors.password?.type === 'minLength' && (
                <p className="text-xs text-destructive">Minimum 12 characters</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setValue('role', v as 'admin' | 'viewer')}>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="department">Department</Label>
              <Input id="department" {...register('department')} placeholder="Engineering" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="slack_handle">Slack handle</Label>
              <Input id="slack_handle" {...register('slack_handle')} placeholder="@jane" />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="slack_user_id">Slack user ID</Label>
            <Input
              id="slack_user_id"
              {...register('slack_user_id')}
              placeholder="U0123456 — used for @mention attribution"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Find in Slack: click profile → ⋯ → Copy member ID
            </p>
          </div>

          {createUser.error && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {(createUser.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending ? 'Creating…' : 'Create user'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ user, currentUserId }: { user: PublicUser; currentUserId: number }) {
  const updateUser = useUpdateUser();
  const [pending, setPending] = useState<string | null>(null);

  const isSelf = user.id === currentUserId;

  async function promote() {
    setPending('promote');
    await updateUser.mutateAsync({ id: user.id, role: 'admin' });
    setPending(null);
  }

  async function demote() {
    setPending('demote');
    await updateUser.mutateAsync({ id: user.id, role: 'viewer' });
    setPending(null);
  }

  async function toggleActive() {
    setPending('active');
    await updateUser.mutateAsync({ id: user.id, is_active: !user.is_active });
    setPending(null);
  }

  return (
    <tr className="hover:bg-muted/20">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            {user.avatar_url && <AvatarImage src={user.avatar_url} alt={user.full_name} />}
            <AvatarFallback className="text-xs">{initials(user.full_name)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium leading-none">
              {user.full_name}
              {isSelf && (
                <span className="ml-2 text-xs text-muted-foreground font-normal">(you)</span>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>{user.role}</Badge>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {user.department ?? '—'}
      </td>
      <td className="px-4 py-3 text-sm font-mono text-muted-foreground">
        {user.slack_handle ?? user.slack_user_id ?? '—'}
      </td>
      <td className="px-4 py-3">
        <Badge variant={user.is_active ? 'success' : 'muted'}>
          {user.is_active ? 'active' : 'inactive'}
        </Badge>
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
        {relativeTime(user.last_login_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {!isSelf && user.role === 'viewer' && (
            <Button
              variant="ghost"
              size="icon"
              title="Promote to admin"
              disabled={pending !== null}
              onClick={promote}
            >
              <ShieldCheck className="h-4 w-4" />
            </Button>
          )}
          {!isSelf && user.role === 'admin' && (
            <Button
              variant="ghost"
              size="icon"
              title="Demote to viewer"
              disabled={pending !== null}
              onClick={demote}
            >
              <ShieldOff className="h-4 w-4" />
            </Button>
          )}
          {!isSelf && (
            <Button
              variant="ghost"
              size="icon"
              title={user.is_active ? 'Deactivate' : 'Reactivate'}
              disabled={pending !== null}
              onClick={toggleActive}
            >
              {user.is_active ? (
                <UserX className="h-4 w-4 text-destructive" />
              ) : (
                <UserCheck className="h-4 w-4 text-success" />
              )}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export function UsersPage() {
  const { data: users, isLoading } = useUsers();
  const { data: me } = useMe();
  const [inviteOpen, setInviteOpen] = useState(false);

  const activeCount = (users ?? []).filter((u) => u.is_active).length;
  const adminCount = (users ?? []).filter((u) => u.role === 'admin' && u.is_active).length;

  const currentUserId = me?.user.id ?? -1;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            {activeCount} active · {adminCount} admin{adminCount === 1 ? '' : 's'}
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Plus className="h-4 w-4" />
          Invite user
        </Button>
      </header>

      {isLoading ? (
        <TableSkeleton widths={['w-40', 'w-16', 'w-24', 'w-24', 'w-16', 'w-24', 'w-16']} rows={6} />
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Slack</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last login</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(users ?? []).map((user) => (
                <UserRow key={user.id} user={user} currentUserId={currentUserId} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
